import {
	Box,
	Button,
	debounce,
	Dialog,
	Divider,
	Grid,
	IconButton,
	SelectChangeEvent,
	Snackbar,
	Stack,
	Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import TeradataIcon from '../../assets/icon-teradata.svg';
import DDHIcon from '../../assets/icon-ddh.svg';
import PowerBiIcon from '../../assets/PowerBI.svg';
import { ReactComponent as PlusSymbolIcon } from '../../assets/plus-blue.svg';
import { ReactComponent as BulkUploadIcon } from '../../assets/bulk-upload-dp.svg';
import { CustomizedDataGrid } from '../customized_data_grid';
import { Column } from 'react-data-grid';
import Loader from '../Loader/loader';
import {
	getSearchScheduleJobNameList,
	getTeradataJobNameList,
	linkAddScheduleToProject,
	getAddedSchedules,
	getAddedScheduleSearchFilterColumn,
	getAllDependentJobs
} from '../../service/service';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';
import useStyles from './data-pipeline-styles';
import { dependInfoTransformData } from 'utils/common-util';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import CustomDropdown from 'components/Dropdown';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import AddIcon from '@mui/icons-material/Add';
import OutlinedButton from 'components/Button/OutlinedButton';
import CloseIcon from '@mui/icons-material/Close';
import WidgetCardContainer from 'components/Widgets/WidgetCardContainer';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import TableCheckbox from 'components/Table/TableCheckbox';
import RowTypography from 'components/Table/RowTypography';
import DialogModal from 'components/Modal/DialogModal';
import LoaderComponent from 'components/Loader';
import eventEmitter from "SnackBarEventEmitter"
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/arrow-triangle-down.svg';
import { ReactComponent as SuccessTicketIcon } from '../../assets/icon-vector.svg';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { DataPipelineModal } from './modal-component';
import { checkRole } from 'utils/roleUtils';
import { JobBulkUpload } from './BulkUpload';
import { useTheme } from "@mui/material/styles";
import { convertDayTime, convertTimeToNumber, dropdownSxProps, normalizeFilter, parseFilter, transformFilterOptions, getStatusLabel, getSchedulerDisplayName, getSchedulerLabel, getCyclicLabel, formatScheduleInfo } from './DataPipelineHelper';

import { autocompleteSxProps, autocompleteTextFieldProps } from 'utils/dataGridSharedStyles';


interface Row {
	id: number;
	selected: boolean;
	jobName: string;
	scheduledDays: string;
	scheduledTimes: string;
	runWindow: string;
	frequency: string;
	SLAODate: string;
	SLATime: string;
	Scheduler: string;
	jobId: string;
	scheduleInfo: string;
	cyclic: string;
	dsName: string;
	dsId: string;
	enabled: boolean;
	workspaceName: string;
	capacityName: string;
	jobStatus: string;
}

interface AddedScheduleData {
	Job_Name: string;
	jobId: number;
	jobName: string;
	Platform_Id: number;
	startTime: string;
	normalCompletionTime: number;
	scheduler: string;
	dependentData: any;
	dependInfo: any;
	isParent: boolean;
	hasDependent: boolean;
	isExpanded: boolean;
	children: AddedScheduleData[];
	className: string;
	eleLevel: string;
	isChildren?: boolean;
	Dep_Job_Name?: string;
	Project_Schedule_ID: number;
	Primary_Project_Schedule_Id: number;
	Days: number;
	Start_Time: number;
	Normal_Completion_Time: number;
	Scheduler_Name: string;
	schedulerName: string;
	slaDate: any;
	slaTime: number;
	platformId: number;
	scheduleInfo: string;
	priCyclic: string;
	priJobStatus: string;
	depJobStatus: string;
	jobCondition: string
}

function getColumns(onCheckboxChange: (e: any, row: any) => void): Array<Column<Row>> {
	return [
		{
			key: 'selected',
			name: 'HeaderCheckbox',
			formatter: ({ row }) => (
				<TableCheckbox
					checked={row.selected}
					onChange={(e) => onCheckboxChange(e, row)}
				/>
			),
			width: 40,
			maxWidth: 100
		},
		{
			key: 'jobName',
			name: 'Jobs',
			formatter(props) {
				return <RowTypography>{props.row.jobName ? props.row.jobName : '--'}</RowTypography>;
			},
			minWidth: 200,
			maxWidth: 550
		},
		{
			key: 'scheduleInfo',
			name: 'Schedule Info',
			formatter(props) {
				return (
					<RowTypography textTransform="capitalize">{props.row.scheduleInfo ? props.row.scheduleInfo.replace('At 0 minutes past the hour, ', '') : '--'}</RowTypography>
				);
			},
			minWidth: 200,
			maxWidth: 550
		},
		{
			key: 'Scheduler',
			name: 'Scheduler',
			formatter(props) {
				const label = getSchedulerLabel(props.row.Scheduler);
				return <RowTypography>{label}</RowTypography>;
			},
			maxWidth: 250
		},
		{
			key: 'cyclic',
			name: 'Cyclic',
			formatter(props) {
				const cyclicLabel = getCyclicLabel(props.row.cyclic, props.row.Scheduler);
				return <RowTypography>{cyclicLabel}</RowTypography>;
			},
			maxWidth: 250
		},
		{
			key: 'jobStatus',
			name: 'Job Status',
			formatter(props) {
				const statusLabel = getStatusLabel(props.row.jobStatus);
				return <RowTypography>{statusLabel}</RowTypography>;
			},
			maxWidth: 250
		}
	];
}

function getPowerBiColumns(onCheckboxChange: (e: any, row: any) => void): Array<Column<Row>> {
	return [
		{
			key: 'selected',
			name: 'HeaderCheckbox',
			formatter: ({ row }) => (
				<TableCheckbox
					checked={row.selected}
					onChange={(e) => onCheckboxChange(e, row)}
				/>
			),
			width: 40,
			maxWidth: 100
		},
		{
			key: 'dsName',
			name: 'Jobs',
			formatter(props) {
				return <RowTypography title={props.row.dsName}>{props.row.dsName ? props.row.dsName : '--'}</RowTypography>;
			},
			minWidth: 200,
			maxWidth: 550
		},
		{
			key: 'capacityName',
			name: 'Capacity',
			formatter(props) {
				return <RowTypography title={props.row.capacityName}>{props.row.capacityName ? props.row.capacityName : '--'}</RowTypography>;
			},
			minWidth: 200,
			maxWidth: 450
		},
		{
			key: 'workspaceName',
			name: 'Workspace',
			formatter(props) {
				return <RowTypography title={props.row.workspaceName}>{props.row.workspaceName ? props.row.workspaceName : '--'}</RowTypography>;
			},
			minWidth: 200,
			maxWidth: 450
		},
		{
			key: 'scheduleInfo',
			name: 'Schedule Info',
			formatter(props) {
				return (
					<RowTypography textTransform="capitalize"
						title={props.row.scheduleInfo ? props.row.scheduleInfo : '--'}>{props.row.scheduleInfo ? props.row.scheduleInfo : '--'}</RowTypography>
				);
			},
			maxWidth: 550
		},
		{
			key: 'enabled',
			name: 'Enabled',
			formatter(props) {
				return <RowTypography>{props.row.enabled ? 'Yes' : 'No'}</RowTypography>;
			},
			maxWidth: 250
		},
	];
}

function getAddedScheduleColumns(toggleSubRow: (name: string) => void, isAllowed: boolean, handleTableAction: (row: AddedScheduleData, action: string) => void): Array<Column<AddedScheduleData>> {
	return [
		{
			key: 'jobName',
			name: 'Jobs',
			formatter({ row, isCellSelected }) {
				const hasChildren = row.isParent && row.hasDependent;
				return (
					<div className={[row.className, row.eleLevel, row.isChildren ? 'child-view' : 'parent-view', hasChildren ? 'has-children' : 'single'].join(' ')}>
						{hasChildren && (
							<CellExpanderFormatter
								isCellSelected={isCellSelected}
								expanded={row.isExpanded === true}
								onCellExpand={() => {
									toggleSubRow(String(row.jobName));
								}}
							/>
						)}
						{'   '}
						<Link to="" state={row}>
							<RowTypography title={row.jobName}>{row.jobName}</RowTypography>
						</Link>{' '}
					</div>
				);
			},
			minWidth: 300
		},
		{
			key: 'platformId',
			name: 'Platform',
			formatter(props) {
				const getPlatformIcon = () => {
					if (props.row.platformId === 1 || props.row.platformId === 2) {
						return <img src={TeradataIcon} alt="Teradata" style={{ width: '18px', verticalAlign: 'middle' }} />;
					} else if (props.row.platformId === 3) {
						return <img src={DDHIcon} alt="DDH" style={{ width: '18px', verticalAlign: 'middle' }} />;
					} else {
						return <img src={PowerBiIcon} alt="Power BI" style={{ width: '18px', verticalAlign: 'middle' }} />;
					}
				};
				const getPlatformName = () => {
					if (props.row.platformId === 1) {
						return 'Teradata';
					} else if (props.row.platformId === 2) {
						return 'BODS';
					} else if (props.row.platformId === 3) {
						return 'DDH';
					} else {
						return '';
					}
				};
				return (
					<RowTypography>
						{getPlatformIcon()} {' '}
						{getPlatformName()}
					</RowTypography>
				);
			},
			minWidth: 150
		},
		{
			key: 'scheduleInfo',
			name: 'Schedule Info',
			formatter(props) {
				const formattedInfo = formatScheduleInfo(props.row.scheduleInfo);
				return (
					<RowTypography textTransform="capitalize" title={formattedInfo}>
						{formattedInfo}</RowTypography>
				);
			},
			minWidth: 200,
			maxWidth: 500
		},

		{
			key: 'schedulerName',
			name: 'Scheduler',
			formatter(props) {
				const label = getSchedulerDisplayName(props.row);
				return (
					<RowTypography>{label}</RowTypography>
				);
			},
			minWidth: 150
		},
		{
			key: 'priJobStatus',
			name: 'Job Status',
			formatter(props) {
				const status = getStatusLabel(props.row.priJobStatus, props.row.depJobStatus);
				return (
					<RowTypography>
						{status}
					</RowTypography>
				);
			},
			minWidth: 150
		},
		{
			key: 'jobCondition',
			name: 'Job Condition',
			formatter(props) {
				return (
					<RowTypography>
						{props.row.jobCondition ? props.row.jobCondition : '--'}
					</RowTypography>
				);
			},
			minWidth: 150
		},
		{
			key: 'priCyclic',
			name: 'Cyclic',
			formatter(props) {
				const cyclicLabel = getCyclicLabel(props.row.priCyclic);
				return (
					<RowTypography>
						{cyclicLabel}
					</RowTypography>
				);
			},
			minWidth: 150
		},
		{
			key: 'action',
			name: 'Actions',
			formatter(props: any) {
				return (
					<Stack direction="row" alignItems="center" height="100%">
						{isAllowed && (
							<IconButton onClick={() => handleTableAction(props.row, 'delete')} title="Click to Delete">
								<DeleteIcon />
							</IconButton>
						)}
					</Stack>
				);
			},
			minWidth: 100
		}

	];
}

const AddSchedules = () => {
	const theme = useTheme();
	const colors = theme.palette;
	const [controller] = usePepwiseAuthController();
	const { role } = controller;

	interface Filter {
		filterType: string;
		keywords: string | Array<string>;
		fromTime?: any;
		fromTimeValue?: string;
		toTime?: any;
		toTimeValue?: string;
		id?: number;
		operation?: string;
		type?: string;
		subType?: string;
	}

	const { prgmid, prjid, prgname, prjname } = useParams();

	let selectedRowsId: any = [];

	const platformList = ['All', 'Teradata', 'BODS', 'DDH'];

	const [filterCriteriaList, setFilterCriteriaList] = React.useState<any>(['Scheduler Name', 'Job Name']);

	const [addedSchedulesList, setAddedSchedulesList] = useState<AddedScheduleData[]>([]);
	const defaultColumnNames = ['HeaderCheckbox', 'Jobs', 'Schedule Info', 'Scheduler', 'Cyclic', 'Job Status'];
	const PowerBiColumnNames = ['HeaderCheckbox', 'Jobs', 'Capacity', 'Workspace', 'Schedule Info', 'Enabled'];

	const [selectedRows, setSelectedRows] = useState<Row[]>([]);
	const [tableDataList, setTableDataList] = useState<Row[]>([]);
	const [jobDataList, setJobDataList] = useState<any[]>([]);
	const [jobNameList, setJobNameList] = useState<any[]>([]);
	const [datasetList, setDatasetList] = useState<any[]>([]);
	const [workspaceList, setWorkspaceList] = useState<any[]>([]);
	const [capacityList, setCapacityList] = useState<any[]>([]);
	const [datasetNameList, setDatasetNameList] = useState<any[]>([]);
	const [workspaceNameList, setWorkspaceNameList] = useState<any[]>([]);
	const [capacityNameList, setCapacityNameList] = useState<any[]>([]);
	const [platformName, setPlatformName] = useState<string>('All');

	const [requestBody, setRequestBody] = React.useState<any>();
	const [successMsg, setSuccessMsg] = React.useState<string>('');
	const [successCount, setSuccessCount] = React.useState<number>(0);

	const [hasDaysAdded, setHasDaysAdded] = React.useState<boolean>();
	const [hasTimeAdded, setHasTimeAdded] = React.useState<boolean>();
	const [hasSchedulerAdded, setHasSchedulerAdded] = React.useState<boolean>(true);

	const [tableLoading, setTableLoading] = React.useState<boolean>();
	const [addProjectLoading, setAddProjectLoading] = React.useState<boolean>();
	const [openModal, setOpenModal] = useState<any>({ isOpen: false });
	const [deleteModal, setDeleteModal] = useState<any>({ isOpen: false });
	const [toast, setToast] = useState<any>({
		open: false,
		message: ''
	});

	const [selectedJobName, setSelectedJobName] = React.useState<boolean>(false);
	const [selectedDatasetName, setSelectedDatasetName] = React.useState<boolean>(false);
	const [selectedWorkspaceName, setSelectedWorkspaceName] = React.useState<boolean>(false);
	const [selectedCapacityName, setSelectedCapacityName] = React.useState<boolean>(false);
	const [searchScheduleBtn, setSearchScheduleBtn] = React.useState<boolean>();
	const [jobSearchKeyword, setJobSearchKeyword] = React.useState<string>('');
	const [datasetSearchKeyword, setDatasetSearchKeyword] = React.useState<string>('');
	const [workspaceSearchKeyword, setWorkspaceSearchKeyword] = React.useState<string>('');
	const [capacitySearchKeyword, setCapacitySearchKeyword] = React.useState<string>('');
	const [searchJobNameInputLoading, setSearchJobNameInputLoading] = React.useState<boolean>();
	const [searchDatasetInputLoading, setSearchDatasetInputLoading] = React.useState<boolean>();
	const [searchWorkspaceInputLoading, setSearchWorkspaceInputLoading] = React.useState<boolean>();
	const [searchCapacityInputLoading, setSearchCapacityInputLoading] = React.useState<boolean>();

	const [bulkUploadModal, setBulkUploadModal] = React.useState<boolean>(false);

	const [filterCriteria, setFilterCriteria] = React.useState<Filter[]>([]);

	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState('1');
	const [pageSize, setPageSize] = useState('100');

	const [paginationDataSchedule, setPaginationDataSchedule] = useState({ totalElements: 0, totalPages: 0 });
	const [pageSchedule, setPageSchedule] = useState('1');
	const [pageSizeSchedule, setPageSizeSchedule] = useState('100');
	const [scheduleTableLoading, setScheduleTableLoading] = React.useState<boolean>();
	const [dependentInfoLoading, setDependentInfoLoading] = React.useState<boolean>();
	let reqRows: Row[] = [];
	let tempSelectedRows: any = [];

	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: 'desc' });
	const [filterInfo, setFilterInfo] = useState([]);
	const [filterData, setFilterData] = useState<Row[]>([]);

	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);

	eventEmitter.on('showSnackbar', (message: string) => {
		setTableLoading(false);
		setScheduleTableLoading(false)
		setSearchJobNameInputLoading(false)
		setAddProjectLoading(false)
	});

	const handleRowCheckboxChange = (e: any, row: any) => {
		const checked = e.target.checked;
		const updatedRow = { ...row, selected: checked };
		const headerCheckboxInput = document.getElementById('HeaderCheckbox') as HTMLInputElement;
		let tempSelectedRows = [...selectedRows];
		
		if (checked) {
			let result = tempSelectedRows.filter(
				(obj: any) => obj.jobId === updatedRow.jobId
			);
			if (!result.length) {
				tempSelectedRows.push(updatedRow);
				selectedRowsId.push(updatedRow.id);
			}
		} else {
			selectedRowsId.splice(selectedRowsId.indexOf(row.id), 1);
			if (headerCheckboxInput) {
				headerCheckboxInput.checked = false;
			}
		}

		if (tempSelectedRows.length == tableDataList.length) {
			if (headerCheckboxInput) {
				headerCheckboxInput.checked = true;
			}
		}
		
		setSelectedRows(tempSelectedRows);
		setTableDataList((prevRows) =>
			prevRows.map((prevRow) => (prevRow.id === updatedRow.id ? updatedRow : prevRow))
		);
	};

	const handlePowerBiRowCheckboxChange = (e: any, row: any) => {
		const checked = e.target.checked;
		const updatedRow = { ...row, selected: checked };
		const headerCheckboxInput = document.getElementById('HeaderCheckbox') as HTMLInputElement;
		let tempSelectedRows = [...reqRows];
		if (checked) {
			let result = tempSelectedRows.filter(
				(obj: any) => obj.dsId === updatedRow.dsId
			);
			if (!result.length) {
				tempSelectedRows.push(updatedRow);
				selectedRowsId.push(updatedRow.id);
			}
		} else {
			selectedRowsId.splice(selectedRowsId.indexOf(row.id), 1);
			if (headerCheckboxInput) {
				headerCheckboxInput.checked = false;
			}
		}

		tempSelectedRows = tempSelectedRows.filter((eachRow: any) => {
			return selectedRowsId.includes(eachRow.id);
		});
		if (tempSelectedRows.length == tableDataList.length) {
			if (headerCheckboxInput) {
				headerCheckboxInput.checked = true;
			}
		}
		reqRows = tempSelectedRows;
		setSelectedRows(tempSelectedRows);
		setTableDataList((prevRows) =>
			prevRows.map((prevRow) => (prevRow.id === updatedRow.id ? updatedRow : prevRow))
		);
	};

	const defaultAddedScheduleColumnNames = [
		'Jobs',
		'Platform',
		'Schedule Info',
		'Scheduler',
		'Job Status',
		'Job Condition',
		'Cyclic',
		'Actions'
	];

	const handleTableAction = (props: any, type: string) => {
		const dta = { ...props, Project_Id: [Number(prjid)] }
		if (type == 'delete') {
			setDeleteModal({
				isOpen: true,
				type: 'Schedule',
				action: 'DELETE',
				from: 'delete',
				data: [dta]
			});
		}
	};

	const getToastData = (data: any) => {
		setToast({
			...toast,
			message: data.data.message
		});
	};

	const handleClose = () => {
		setToast({
			...toast,
			open: false
		});
	};

	const handleEditClose = (closeType: any) => {
		setDeleteModal({
			...openModal,
			isOpen: false,
			closeType: closeType
		});
	};

	const passInfoToParent = (data: any) => {
		getAddedScheduleList();
	};

	function compareTimes(time1: string, time2: string) {
		const format = 'HH:mm';

		// Parse both times using moment
		const momentTime1 = moment(time1, format);
		const momentTime2 = moment(time2, format);
		const momentTime = momentTime1.add(30, 'minutes');

		// Compare and return true only if time1 is strictly greater than time2
		return momentTime.isAfter(momentTime2);
	}

	function toggleSubRow(name: string) {
		addSchData = addSchData.length ? addSchData : addedSchedulesList;
		const rows = addSchData;
		const rowIndex = rows.findIndex((r: any) => r.jobName == name);
		const row = rows[rowIndex];
		const newRows = [...rows];
		let children: AddedScheduleData[] = [];
		let childRows: AddedScheduleData[] = [];
		if (row.isExpanded === false) {
			const boxRows: any = localStorage.getItem('boxRows');
			const storageRows = boxRows ? JSON.parse(boxRows) : [];
			let hasStrRow = false;
			storageRows.filter((strRow: any) => {
				if (strRow.jobId == row.jobId && strRow.projectId == Number(prjid)) {
					hasStrRow = true;
					newRows.splice(Number(rowIndex) + 1, 0, ...strRow.children);
					newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children: strRow.children };
					const newUpdatedRows = [...newRows];
					setAddedSchedulesList(newUpdatedRows);
					addSchData = newUpdatedRows
				}
			});
			if (!hasStrRow) {
				const params = {
					parentId: row.jobId,
					platformId: row.platformId
				}
				setDependentInfoLoading(true)
				const dependentPromise = getAllDependentJobs(params);
				dependentPromise.then((res) => {
					setDependentInfoLoading(false)
					if (res.success) {
						for (const [, value] of Object.entries(res.data)) {
							childRows = dependInfoTransformData(value);
						}
						children = childRows.map((x: any) => {
							return { ...x, isChildren: true };
						});
						newRows.splice(Number(rowIndex) + 1, 0, ...children);
						newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children };
						const newUpdatedRows = [...newRows];
						setAddedSchedulesList(newUpdatedRows);
						addSchData = newUpdatedRows
						const stBoxRows: any = localStorage.getItem('boxRows');
						const tempRows = [{
							jobId: row.jobId,
							projectId: Number(prjid),
							children: children
						}]
						if (stBoxRows) {
							let tempStorageBoxRows = JSON.parse(stBoxRows)
							tempStorageBoxRows = tempStorageBoxRows.concat(tempRows);
							localStorage.setItem('boxRows', JSON.stringify(tempStorageBoxRows))
						} else {
							localStorage.setItem('boxRows', JSON.stringify(tempRows))
						}
					}
				})
			}
		} else {
			newRows.splice(Number(rowIndex) + 1, row.children.length);
			newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded };
			const newUpdatedRows = [...newRows];
			setAddedSchedulesList(newUpdatedRows);
			addSchData = newUpdatedRows
		}
	}
	let addSchData: any = [];

	const getAddedScheduleList = async (
		page: number = 1,
		size: number = 100,
		filter: any = {},
		sortingInfo: any = {}
	) => {
		const colFilterInfo = normalizeFilter(filter);
		let params = {
			projectId: Number(prjid),
			page,
			size,
			filter: colFilterInfo,
			sortBy: sortingInfo.sortBy || '',
			orderBy: sortingInfo.sortDir || ''
		};
		setScheduleTableLoading(true);
		const result = await getAddedSchedules(params);
		if (result.success) {
			setScheduleTableLoading(false);
			const jobArr = result.data.selectedJobData;
			let reqData: any = [];
			jobArr.map((data: any, index: number) => {
				data['className'] = 'l1';
				data['isExpanded'] = false;
				data['dependInfo'] = data.dependInfo?.length ? data.dependInfo : [];
				data['isParent'] = true;
				data['eleLevel'] = 'l' + index;
				reqData.push(data);
			});

			setAddedSchedulesList(reqData);
			addSchData = reqData;

			if (result.data.filterOptions) {
				const filterOptionCon = transformFilterOptions(result.data.filterOptions, false);
				setFilterData(filterOptionCon);
			}

			setPaginationDataSchedule(result.data.page);
			setPageSchedule(result.data.page.currentPage);
			setPageSizeSchedule(result.data.page.size);
		}
	};

	const handleSorting = (sortingInfo: any) => {
		if (parseFilter(sortingInfo).sortBy == 'platform') {
			let parsedData = parseFilter(sortingInfo);
			parsedData['sortBy'] = 'Platform_Id';
			setSortingInfo(parsedData);
		} else {
			setSortingInfo(sortingInfo);
			getAddedScheduleList(Number(page), Number(pageSize), parseFilter(filterInfo), sortingInfo);
		}
	};

	const handleJobNameFilter = (filterData: any, parsedData: any) => {
		let filterFormat: any = {
			...parsedData,
			Project_Schedule_ID: []
		};
		for (const data of parsedData['Job_Name']) {
			let filteredData: any = addedSchedulesList.filter((fil: any) => fil.Job_Name == data);
			if (filteredData.length) {
				filterFormat.Project_Schedule_ID = filteredData.map((data: any) => data.Project_Schedule_ID);
			}
		};
		if (!Object.keys(filterFormat).includes('Job_Name')) {
			filterFormat = {};
		}
		setFilterInfo(filterFormat);
		getAddedScheduleList(Number(page), Number(pageSize), filterFormat, sortingInfo);
	};

	const handlePlatformFilter = (filterData: any, parsedData: any) => {
		parsedData['Platform_Id'] = [];
		for (const data of parsedData['platform']) {
			if (data === 'Teradata') {
				parsedData['Platform_Id'].push(1);
			}
		};
		setFilterInfo(filterData);
		delete parsedData.platform;
		getAddedScheduleList(Number(page), Number(pageSize), parsedData, sortingInfo);
	};

	const handleStartTimeFilter = (filterData: any, parsedData: any) => {
		const start = parsedData['Start_Time'];
		parsedData['Days'] = [];
		parsedData['Start_Time'] = [];
		parsedData['Project_Schedule_ID'] = [];
		
		for (const data of start) {
			let obj = convertDayTime(data);
			parsedData['Start_Time'].push(obj.time);
			parsedData['Days'].push(obj.day);
		};
		
		for (const [index, data] of parsedData['Days'].entries()) {
			let filteredData: any = addedSchedulesList.filter(
				(fil: any) => fil.Days == data && fil.Start_Time == parsedData['Start_Time'][index]
			);
			if (filteredData.length) {
				parsedData.Project_Schedule_ID = filteredData.map((data: any) => data.Project_Schedule_ID);
			}
		};
		setFilterInfo(filterData);
		getAddedScheduleList(Number(page), Number(pageSize), parsedData, sortingInfo);
	};

	const handleNormalCompletionTimeFilter = (filterData: any, parsedData: any) => {
		const start = parsedData['normalCompletionTime'];
		parsedData['Normal_Completion_Time'] = [];
		parsedData['Project_Schedule_ID'] = [];
		
		for (const data of start) {
			parsedData['Normal_Completion_Time'].push(convertTimeToNumber(data));
		};
		
		for (const data of parsedData['Normal_Completion_Time']) {
			let filteredData: any = addedSchedulesList.filter((fil: any) => fil.Normal_Completion_Time == data);
			if (filteredData.length) {
				parsedData.Project_Schedule_ID = filteredData.map((data: any) => data.Project_Schedule_ID);
			}
		};
		setFilterInfo(filterData);
		delete parsedData.normalCompletionTime;
		getAddedScheduleList(Number(page), Number(pageSize), parsedData, sortingInfo);
	};

	const handleFilter = (filterData: any) => {
		setAddedSchedulesList([]);
		setScheduleTableLoading(true);
		
		if (filterData.Project_Schedule_ID) {
			delete filterData.Project_Schedule_ID;
		}

		const parsedData = parseFilter(filterData);
		const filterKeys = Object.keys(parsedData);

		if (filterKeys.includes('Job_Name')) {
			handleJobNameFilter(filterData, parsedData);
		} else if (filterKeys.includes('platform')) {
			handlePlatformFilter(filterData, parsedData);
		} else if (filterKeys.includes('Start_Time')) {
			handleStartTimeFilter(filterData, parsedData);
		} else if (filterKeys.includes('normalCompletionTime')) {
			handleNormalCompletionTimeFilter(filterData, parsedData);
		} else {
			setFilterInfo(filterData);
			getAddedScheduleList(Number(page), Number(pageSize), parsedData, sortingInfo);
		}
	};

	const handleHeaderSelectAll = (event: any) => {
		selectedRowsId = [];
		let tempSelectedRows: any = [];
		for (const row of tableDataList) {
			row.selected = event.target.checked;
			if (event.target.checked) {
				selectedRowsId.push(row.id);
			}
		}
		if (event.target.checked) {
			tempSelectedRows = tableDataList;
		}
		setSelectedRows(tempSelectedRows);

		setTableDataList([...tableDataList]);

		reqRows = tempSelectedRows;

		let inputElements: any = document.getElementsByClassName('row-checkbox');

		for (const inputElement of inputElements) {
			if (inputElement instanceof HTMLInputElement) {
				inputElement.checked = event.target.checked;
			}
		}
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: string) => {
		searchScheduleFunc(requestBody, value, pageSize);
	};
	const handlepageSizeChange = (event: any) => {
		searchScheduleFunc(requestBody, page, event.target.value);
	};

	const handlePageChangeSchedule = (event: React.ChangeEvent<unknown>, value: string) => {
		getAddedScheduleList(Number(value), Number(pageSize));
	};

	const handleFilterSearch = async (searchKeyword: any, columnKey: any) => {
		if (searchKeyword?.length >= 3) {
			let obj = {
				columnName: columnKey,
				key: searchKeyword
			};
			const res = await getAddedScheduleSearchFilterColumn(obj);
			if (res.success) {
				setFilterData(prev => ({
					...prev,
					[columnKey]: res.data?.map((obj: any) => obj.name) || []
				}));
			}
		}
	};
	const handlepageSizeChangeSchedule = (event: any) => {
		getAddedScheduleList(Number(page), event.target.value);
	};
	const handleOnClose = (event: any, index: number, autoValue?: any) => {
		if (autoValue !== 'selectOption') return;

		const {
			target: { textContent }
		} = event;
		const filterData = [...filterCriteria];
		filterData[index]['keywords'] = textContent;
		if (textContent) {
			for (const job of jobDataList) {
				if (job.JobName == textContent) {
					filterData[index]['id'] = job.JobId;
				}
			};
		}
		for (const fil of filterData) {
			if (fil.filterType == 'Job Name' && fil.keywords) {
				setSelectedJobName(true);
			}
		};
		setFilterCriteria(filterData);
	};

	const assignIdByFilterType = (filterData: any[], index: number, autoValue: string, selectedFilterType: string) => {
		if (selectedFilterType === 'Job Name') {
			const job = jobDataList.find(j => j.JobName === autoValue);
			if (job) filterData[index]['id'] = job.JobId;
		} else if (selectedFilterType === 'Dataset') {
			const ds = datasetList.find(d => d.NAME === autoValue);
			if (ds) filterData[index]['id'] = ds.ID;
		} else if (selectedFilterType === 'Workspace') {
			const ws = workspaceList.find(w => w.name === autoValue);
			if (ws) filterData[index]['id'] = ws.orgId;
		} else if (selectedFilterType === 'Capacity') {
			const cap = capacityList.find(c => c.displayname === autoValue);
			if (cap) filterData[index]['id'] = cap.capacityId;
		}
	};

	const updateJobNameIds = (filterData: any[]) => {
		const jobNameArr = filterData.filter((obj: any) => obj.filterType === 'Job Name');
		for (const obj of jobNameArr) {
			for (const job of jobDataList) {
				if (job.JobName === obj.keywords) {
					const pos = filterData.findIndex((data: any) => data.keywords === obj.keywords);
					if (pos !== -1) {
						filterData[pos]['id'] = job.JobId;
					}
				}
			}
		}
	};

	const updateSelectionState = (filterData: any[], selectedFilterType: any) => {
		const hasMatchingFilter = filterData.some(fil => fil.filterType === selectedFilterType && fil.keywords);
		if (hasMatchingFilter) {
			if (selectedFilterType === 'Job Name') setSelectedJobName(true);
			else if (selectedFilterType === 'Dataset') setSelectedDatasetName(true);
			else if (selectedFilterType === 'Workspace') setSelectedWorkspaceName(true);
			else if (selectedFilterType === 'Capacity') setSelectedCapacityName(true);
		}
	};

	const handleChangeDynamicKeywords = (event: any, index: number, autoValue?: any, selectedFilterType?: string) => {
		const {
			target: { value }
		} = event;
		const filterData = [...filterCriteria];
		filterData[index]['keywords'] = autoValue ?? value;
		if (autoValue && selectedFilterType) {
			assignIdByFilterType(filterData, index, autoValue, selectedFilterType);
		}

		updateJobNameIds(filterData);
		updateSelectionState(filterData, selectedFilterType);
		setFilterCriteria(filterData);
	};

	const debouncedFetchOptions = useMemo(
		() =>
			debounce(async (filterData, value, type: string, platform: string) => {
				if (type == 'Job Name') {
					jobNameListFunc(platform, filterData[0].keywords, value, filterData);
				}
			}, 500),
		[]
	);

	const onChangeFieldKeyDown = (event: any) => {
		if (!/[0-9a-zA-Z[\]#!*?_-]/.test(event.key)) {
			event.preventDefault();
		}
	};

	const onChangeFieldInput = (event: any, index: number, type: string) => {
		const {
			target: { value }
		} = event;
		const valueTrim = value.trim();
		const filterData = [...filterCriteria];
		filterData[index]['keywords'] = valueTrim;
		setFilterCriteria(filterData);
		setSelectedJobName(false);
		if (valueTrim && valueTrim.length > 0) {
			if (type == 'Job Name') {
				setJobSearchKeyword(valueTrim);
				setSearchJobNameInputLoading(true);
			} else if (type == 'Dataset') {
				setDatasetSearchKeyword(valueTrim);
				setSearchDatasetInputLoading(true);
			} else if (type == 'Workspace') {
				setWorkspaceSearchKeyword(valueTrim);
				setSearchWorkspaceInputLoading(true);
			} else if (type == 'Capacity') {
				setCapacitySearchKeyword(valueTrim);
				setSearchCapacityInputLoading(true);
			}
			debouncedFetchOptions(filterData, valueTrim, type, platformName);
		}
	};

	const handleChangePlatform = (event: SelectChangeEvent<typeof platformName>) => {
		const {
			target: { value }
		} = event;
		setSearchScheduleBtn(false);
		setPlatformName(value);
		setFilterCriteria([]);
		setJobSearchKeyword('');
		setDatasetSearchKeyword('');
		setWorkspaceSearchKeyword('');
		setCapacitySearchKeyword('');
		setJobNameList([]);
		if (value == 'Power BI') {
			setFilterCriteriaList(['Capacity', 'Workspace', 'Dataset'])
			const newFilterData = [
				{ filterType: 'Capacity', operation: 'Equals to (=)', keywords: '' },
				{ filterType: 'Workspace', operation: 'Equals to (=)', keywords: '' },
				{ filterType: 'Dataset', operation: 'Equals to (=)', keywords: '' }
			];
			const insertedFilterCriteria = newFilterData;
			setFilterCriteria([...insertedFilterCriteria]);
		}
		else if (value == 'BODS') {
			const newFilterData = [
				{ filterType: 'Scheduler Name', operation: 'Equals to (=)', keywords: 'Control-M-PGT' },
				{ filterType: 'Job Name', operation: 'Equals to (=)', keywords: '', id: 1 }
			];
			const insertedFilterCriteria = newFilterData;
			setFilterCriteria([...insertedFilterCriteria]);
			setFilterCriteriaList(['Scheduler Name', 'Job Name'])
		}
		else {
			const newFilterData = [
				{ filterType: 'Scheduler Name', operation: 'Equals to (=)', keywords: 'All' },
				{ filterType: 'Job Name', operation: 'Equals to (=)', keywords: '', id: 1 }
			];
			const insertedFilterCriteria = newFilterData;
			setFilterCriteria([...insertedFilterCriteria]);
			setFilterCriteriaList(['Scheduler Name', 'Job Name'])
		}
	};

	const handleChangeDynamicFilter = (event: any, index: number) => {
		const { target: { value } } = event;
		const filterData = [...filterCriteria];
		filterData[index].filterType = value;
		if (value == 'Days') {
			filterData[index].keywords = []
			filterData[index]['id'] = index;
		} else if (value == 'Job Name') {
			filterData[index].id = index;
		} else if (value == 'Start Time') {
			filterData[index].fromTime = '';
			filterData[index].toTime = '';
			filterData[index].fromTimeValue = '';
			filterData[index].toTimeValue = '';
		}
		setHasSchedulerAdded(false);
		setHasDaysAdded(false);
		setHasTimeAdded(false);
		for (const fil of filterData) {
			if (fil.filterType == 'Days') {
				setHasDaysAdded(true);
			} else if (fil.filterType == 'Start Time') {
				setHasTimeAdded(true);
			} else if (fil.filterType == 'Scheduler Name') {
				setHasSchedulerAdded(true);
			}
		}
		setFilterCriteria(filterData);
	};

	const handleAddFilter = (index: any) => {
		let newFilterData = { filterType: 'Job Name', operation: 'Equals to (=)', keywords: '' };
		if (!hasSchedulerAdded) {
			newFilterData = { filterType: 'Scheduler Name', operation: 'Equals to (=)', keywords: '' };
		}
		if (platformName == 'Power BI') {
			newFilterData = { filterType: 'Capacity', operation: 'Equals to (=)', keywords: '' };
		}
		if (index == -1) {
			filterCriteria.unshift(newFilterData);
		} else {
			filterCriteria.push(newFilterData);
		}
		const insertedFilterCriteria = filterCriteria;
		setFilterCriteria([...insertedFilterCriteria]);
	};

	const handleRemoveFilter = (index: any) => {
		const filterData = [...filterCriteria];
		if (filterData[index].filterType == 'Days') {
			setHasDaysAdded(false);
		} else if (filterData[index].filterType == 'Start Time') {
			setHasTimeAdded(false);
		} else if (filterData[index].filterType == 'Scheduler Name') {
			setHasSchedulerAdded(false);
		}
		filterData.splice(index, 1);
		setFilterCriteria([...filterData]);
	};

	const handleClear = () => {
		setSelectedJobName(false);
		setSelectedDatasetName(false);
		setSelectedWorkspaceName(false);
		setSelectedCapacityName(false);
		setSearchScheduleBtn(false);
		setHasDaysAdded(false);
		setHasTimeAdded(false);
		setJobSearchKeyword('');
		setDatasetSearchKeyword('');
		setWorkspaceSearchKeyword('');
		setCapacitySearchKeyword('');
		setJobNameList([]);
		setDatasetNameList([]);
		setWorkspaceNameList([]);
		setCapacityNameList([]);
		let filterData = [
			{ filterType: 'Scheduler Name', operation: 'Equals to (=)', keywords: 'All' },
			{ filterType: 'Job Name', operation: 'Equals to (=)', keywords: '', id: 1 }
		];
		if (platformName == 'Power BI') {
			filterData = [
				{ filterType: 'Capacity', operation: 'Equals to (=)', keywords: '' },
				{ filterType: 'Workspace', operation: 'Equals to (=)', keywords: '' },
				{ filterType: 'Dataset', operation: 'Equals to (=)', keywords: '' }
			];
		}
		setFilterCriteria(filterData);
	};

	const onSubmitSearchFetchJobIds = () => {
		setSelectedRows([]);
		handleOnSubmit(filterCriteria);
	}

	const openBulkUploadModal = () => {
		setBulkUploadModal(true)
	}

	const updateReqBodyByFilterType = (filter: any, reqBody: any, flags: any) => {
		switch (filter.filterType) {
			case 'Job Name':
				reqBody.jobIds.push(filter.keywords);
				if (selectedJobName && !filter.id) {
					reqBody.jobIds = [];
				}
				break;
			case 'Dataset':
				reqBody.dsName = filter.keywords;
				break;
			case 'Workspace':
				reqBody.wkName = filter.keywords;
				break;
			case 'Capacity':
				reqBody.cpName = filter.keywords;
				break;
			case 'Days':
				if (flags.hasSchedulerType && flags.hasJobName) {
					reqBody.days = filter.keywords.includes('All') ? 'all' : filter.keywords.map((d: string) => d.substring(0, 2).toLowerCase());
				}
				break;
			case 'Start Time':
				if (flags.hasSchedulerType && flags.hasJobName && filter.fromTimeValue && filter.toTimeValue) {
					reqBody.startTime = [filter.fromTimeValue, filter.toTimeValue];
				}
				break;
		}
	};

	const getUpdatedFlagByFilterType = (filter: any, flags: any): any => {
		if (filter.filterType === 'Scheduler Name' && filter.keywords) {
			return { ...flags, hasSchedulerType: true };
		}
		if (filter.filterType === 'Job Name' && filter.keywords) {
			return { ...flags, hasJobName: true };
		}
		if (filter.filterType === 'Dataset' && filter.keywords) {
			return { ...flags, hasDatasetName: true };
		}
		if (filter.filterType === 'Workspace' && filter.keywords) {
			return { ...flags, hasWorkspaceName: true };
		}
		if (filter.filterType === 'Capacity' && filter.keywords) {
			return { ...flags, hasCapacityName: true };
		}
		return flags;
	};

	const processFilterCriteria = (
		filter: any, 
		reqBody: any, 
		hasSchedulerType: boolean, 
		hasJobName: boolean, 
		hasDatasetName: boolean, 
		hasWorkspaceName: boolean, 
		hasCapacityName: boolean) => {
		const flags = { hasSchedulerType, hasJobName, hasDatasetName, hasWorkspaceName, hasCapacityName };
		
		updateReqBodyByFilterType(filter, reqBody, flags);
		return getUpdatedFlagByFilterType(filter, flags);
	};

	const handleOnSubmit = (filterCriterias?: any) => {
		let filterCriteriaData = [];
		if (filterCriterias && filterCriteria.length) {
			filterCriteriaData = filterCriterias;
		}
		let hasJobName = false, hasDatasetName = false, hasWorkspaceName = false, hasCapacityName = false,
			hasSchedulerType = false;
		let reqBody: any = { schedulerType: filterCriteriaData[0]?.keywords, jobIds: [], days: [], startTime: [], projectId: prjid, platformName: platformName };
		if (platformName == 'Power BI') {
			reqBody = { dsName: '', wkName: '', cpName: '' };
		}
		
		for (const filter of filterCriteriaData) {
			const flags = processFilterCriteria(filter, reqBody, hasSchedulerType, hasJobName, hasDatasetName, hasWorkspaceName, hasCapacityName);
			hasSchedulerType = flags.hasSchedulerType || hasSchedulerType;
			hasJobName = flags.hasJobName || hasJobName;
			hasDatasetName = flags.hasDatasetName || hasDatasetName;
			hasWorkspaceName = flags.hasWorkspaceName || hasWorkspaceName;
			hasCapacityName = flags.hasCapacityName || hasCapacityName;
		}

		if (hasJobName || hasDatasetName || hasWorkspaceName || hasCapacityName) {
			setSearchScheduleBtn(true);
			setRequestBody(reqBody);
			searchScheduleFunc(reqBody, '1', '100');
		}
	};

	const addSchedulesToProject = () => {
		if (!selectedRows.length) return;

		const isPowerBI = platformName === 'Power BI';

		const reqBodySch = selectedRows.map((row: any) =>
			isPowerBI ? buildPowerBIBody(row) : buildDefaultBody(row)
		);

		addScheduleToProjectFunc(reqBodySch);
	};

	const buildPowerBIBody = (row: any) => ({
		dsName: row.dsName,
		dsId: row.dsId,
		platformId: 2,
		programId: Number(prgmid),
		projectId: Number(prjid),
		isEnabled: row.enabled
	});

	const buildDefaultBody = (row: any) => ({
		JobName: row.jobName,
		JobId: row.jobId,
		SLAODate: row.SLAODate || 0,
		Scheduler: row.Scheduler,
		SLATime: row.SLATime ? Number(row.SLATime.replace(':', '')) : null,
		ProgramId: Number(prgmid),
		ProjectId: Number(prjid),
		Cyclic: row.cyclic,
		platformName: row.Platform || platformName,
		platformId: row.platformId
	});


	async function addScheduleToProjectFunc(reqBody: any) {
		setAddProjectLoading(true);

		try {
			const res = await linkAddScheduleToProject(reqBody);
			if (res.statusCode == 200) {
				setSuccessMsg(res.data.message);
				setSuccessCount(res.data.addedJobs);
				if (!bulkUploadModal) {
					setOpenModal({
						isOpen: true
					});
				}
			} else if (res.code == 'ERR_BAD_REQUEST') {
				setSuccessMsg('Schedules already added!');
				if (!bulkUploadModal) {
					setOpenModal({
						isOpen: true
					});
				}
			}
		} finally {
			setAddProjectLoading(false);
		}
	}

	async function jobNameListFunc(platformName: any, schedulerType: string, jobName?: string, filterData?: any) {
		const res = await getTeradataJobNameList(platformName, schedulerType, jobName, Number(prjid));
		if (res.success) {
			setJobDataList(res.data.jobNames);
			setSearchJobNameInputLoading(false);
			setJobNameList(res.data.jobNames.map((jn: any) => jn.JobName));
		}
	}

	async function searchScheduleFunc(reqBody: any, pageNumber: string, pageSize: string) {
		setTableLoading(true);
		const res = await getSearchScheduleJobNameList(reqBody, pageNumber, pageSize);
		if (res.success) {
			let schListData: any = res.data.selectedJobData.map((row: any, index: number) => ({
				...row,
				SLAODate: '',
				SLATime: '',
				selected: false,
				id: index
			}));
			setTableDataList(schListData);
			setPaginationData(res.data.page);
			setPage(res.data.page.currentPage);
			setPageSize(res.data.page.size);
		}
		setJobSearchKeyword('');
		setTableLoading(false);
	}

	const getSchedulerOptions = () => {
		if (platformName === 'Teradata') {
			return [
				{ label: 'All', value: 'All' },
				{ label: 'AutoSys', value: 'AutoSys' },
				{ label: 'Control-M', value: 'Control-M' }
			];
		} else if (platformName === 'BODS') {
			return [
				{ label: 'Control-M-PGT', value: 'Control-M-PGT' }
			];
		} else if (platformName === 'DDH') {
			return [
				{ label: 'All', value: 'All' },
				{ label: 'NATIVE', value: 'NATIVE' },
				{ label: 'CUSTOM', value: 'CUSTOM' }
			];
		}
		else if (platformName === 'All') {
			return [
				{ label: 'All', value: 'All' },
				{ label: 'AutoSys', value: 'AutoSys' },
				{ label: 'Control-M', value: 'Control-M' },
				{ label: 'Control-M-PGT', value: 'Control-M-PGT' },
				{ label: 'NATIVE', value: 'Control-M-PGT' },
				{ label: 'CUSTOM', value: 'CUSTOM' }
			];
		}
		return [];
	};

	const handleModalClose = (data: any) => {
		if (searchScheduleBtn) {
			handleAddMore();
		} else {
			setOpenModal({
				isOpen: false
			});
		}
	};

	const handleAddMore = () => {
		setAddedSchedulesList([]);
		setSelectedRows([]);
		setOpenModal({
			isOpen: false
		});
		getAddedScheduleList();
		handleClear();
	};

	const classes = useStyles();

	useEffect(() => {
		getAddedScheduleList();
	}, []);

	useEffect(() => {
		let filterData = [
			{ filterType: 'Scheduler Name', operation: 'Equals to (=)', keywords: 'All' },
			{ filterType: 'Job Name', operation: 'Equals to (=)', keywords: '' }
		];

		if (platformName == 'BODS') {
			filterData = [
				{ filterType: 'Scheduler Name', operation: 'Equals to (=)', keywords: 'Control-M-PGT' },
				{ filterType: 'Job Name', operation: 'Equals to (=)', keywords: '' }
			];
		}
		if (platformName == 'Power BI') {
			filterData = [{ filterType: 'Dataset', operation: 'Equals to (=)', keywords: '' },
			{ filterType: 'Workspace', operation: 'Equals to (=)', keywords: '' },
			{ filterType: 'Capacity', operation: 'Equals to (=)', keywords: '' }
			];
		}
		setFilterCriteria(filterData);
		const activeId = document.getElementById('root');
		if (window.location.pathname.includes('/data-pipeline/search-jobs')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}
	}, [openModal]);

	return (
		<Stack mt={8} p={2} spacing={2} bgcolor="backgroundLight.primary">
			<PageHeader
				icon={DashboardIcon}
				title={[
					{
						text: 'Programs',
						link: '/data-pipeline/programs'
					},
					{
						text: decodeURIComponent(window.location.pathname.split('/')[5]),
						link: `/data-pipeline/programs/${window.location.pathname
							.split('/')[3]
							.replace(/%20/g, ' ')}/${window.location.pathname.split('/')[5].replace(/%20/g, ' ')}`
					},
					{
						text: decodeURIComponent(window.location.pathname.split('/')[6]),
						link: `/data-pipeline/projects/${window.location.pathname
							.split('/')[3]
							.replace(/%20/g, ' ')}/${window.location.pathname
								.split('/')[4]
								.replace(/%20/g, ' ')}/${window.location.pathname
									.split('/')[5]
									.replace(/%20/g, ' ')}/${window.location.pathname.split('/')[6].replace(/%20/g, ' ')}/${(moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD'))}`
					},
					{
						text: 'Add Jobs'
					}
				]}
			/>
			<WidgetCardContainer bgColor="primary.main" sx={{ maxHeight: 300, overflowY: 'auto' }}>
				<Grid item container xs={12} spacing={1}>
					<Grid item xs={2}>
						<Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#e5e5e5', marginTop: '10px' }}>Select Platform</Typography>
					</Grid>
					<Grid item xs={4}>
						<CustomDropdown
							options={platformList.map((platform) => ({
								value: platform,
								label: platform
							}))}
							handleChange={handleChangePlatform}
							value={platformName}
							selectComponentProps={{
								displayEmpty: true,
								sx: dropdownSxProps
							}}
							boxProps={{
								maxWidth: 400
							}}
							dropdownIcon={ArrowDownIcon}
						/>
					</Grid>

					<Grid item xs={6}>
						{/* <Grid item xs={12} md={10} lg={11}> */}
						<Box display="flex" alignItems="right" justifyContent="flex-end">
							<Button
								variant="pepwiseSecondaryOutline2"
								onClick={handleClear}
								sx={{ width: '160px', height: '40px', color: 'rgba(0, 155, 216, 1)', marginRight: '8px', borderColor: 'rgba(0, 155, 216, 1)' }}
							>
								Clear
							</Button>
							<Button
								variant="pepwiseSecondary"
								disabled={
									!(
										selectedJobName ||
										jobSearchKeyword ||
										(selectedDatasetName && selectedWorkspaceName && selectedCapacityName) ||
										(datasetSearchKeyword && workspaceSearchKeyword && capacitySearchKeyword)
									)
								}
								onClick={onSubmitSearchFetchJobIds}
								sx={{ minWidth: '160px', height: '40px', background: 'transparent', marginRight: '8px', color: 'rgba(0, 155, 216, 1)', border: '1px solid rgba(0, 155, 216, 1)', '&:hover': { color: '#fff' } }}
							>
								Run Query
							</Button>
							<Button
								variant="pepwiseSecondaryOutline2"
								disabled={false}
								onClick={openBulkUploadModal}
								sx={{ width: '30px', height: '40px', background: 'transparent', color: 'rgba(0, 155, 216, 1)', border: '1px solid rgba(0, 155, 216, 1)' }}
							>
								<BulkUploadIcon />
							</Button>
						</Box>
					</Grid>
				</Grid>
				<Grid item container xs={12} spacing={1}>
					<Divider style={{ borderColor: 'rgba(113, 117, 143, 1)', width: '100%', margin: '15px 0' }} />
				</Grid>
				<Grid item container xs={12} spacing={1}>
					<Grid item xs={6} style={{ fontSize: '14px', fontWeight: '500', color: 'rgba(169, 173, 203, 1)' }}>Field</Grid>
					<Grid item xs={6} style={{
						fontSize: '14px', fontWeight: '500', color: 'rgba(169, 173, 203, 1)',
						display: 'flex', justifyContent: 'space-between'
					}}>
						Value <button
							type="button"
							onClick={() => handleAddFilter(-1)}
							style={{
								fontSize: '16px',
								fontWeight: 700,
								color: 'rgba(0, 155, 216, 1)',
								cursor: 'pointer',
								background: 'none',
								border: 'none',
								padding: 0
							}}
						>
							+ ADD CLAUSE
						</button>
					</Grid>
				</Grid>
				<Grid item container xs={12}>
					<Grid item container xs={12} spacing={1} display="flex" alignItems="center">
						{filterCriteria?.map((filter: any, idx: number) => {
								// compute noOptionsText values outside of JSX to avoid nested ternaries
								let jobNoOptionsText = 'Search Jobs';
								if (searchJobNameInputLoading === true) {
									jobNoOptionsText = 'Loading...';
								} else if (searchJobNameInputLoading === false) {
									jobNoOptionsText = 'No data';
								}

								let datasetNoOptionsText = 'Search Dataset';
								if (searchDatasetInputLoading === true) {
									datasetNoOptionsText = 'Loading...';
								} else if (searchDatasetInputLoading === false) {
									datasetNoOptionsText = 'No data';
								}

								let workspaceNoOptionsText = 'Search Workspace';
								if (searchWorkspaceInputLoading === true) {
									workspaceNoOptionsText = 'Loading...';
								} else if (searchWorkspaceInputLoading === false) {
									workspaceNoOptionsText = 'No data';
								}

								let capacityNoOptionsText = 'Search Capacity';
								if (searchCapacityInputLoading === true) {
									capacityNoOptionsText = 'Loading...';
								} else if (searchCapacityInputLoading === false) {
									capacityNoOptionsText = 'No data';
								}

								return (
									<React.Fragment key={filter.filterType + idx}>

										<Grid item xs={6}>
											<CustomDropdown
												options={filterCriteriaList.map((name: any) => {
													const isDisabled =
														(name === 'Days' && hasDaysAdded) ||
														(name === 'Start Time' && hasTimeAdded) ||
														(name === 'Scheduler Name' && hasSchedulerAdded);
													return {
														label: name,
														value: name,
														isDisabled
													};
												})}
												value={filter.filterType}
												handleChange={(e) => {
													handleChangeDynamicFilter(e, idx);
												}}
												selectComponentProps={{
													sx: dropdownSxProps
												}}
												dropdownIcon={ArrowDownIcon}
											/>
										</Grid>

										{filter.filterType == 'Scheduler Name' && (
											<Grid item xs={5}>
												<CustomDropdown
													options={getSchedulerOptions()}
													value={filter.keywords}
													handleChange={(e) => {
														handleChangeDynamicKeywords(e, idx);
													}}
													selectComponentProps={{
														displayEmpty: true,
														sx: dropdownSxProps
													}}
													dropdownIcon={ArrowDownIcon}
												/>
											</Grid>
										)}

										{filter.filterType == 'Job Name' && (
											<Grid item xs={5}>
												<CustomAutocomplete
													options={jobNameList}
													noOptionsText={jobNoOptionsText}
													value={filter.keywords}
													onClose={(e, val) => handleOnClose(e, idx, val)}
													onChange={(e, val) => handleChangeDynamicKeywords(e, idx, val, 'Job Name')}
													sx={autocompleteSxProps}
													customTextFieldProps={{
														placeholder: 'Search Job Name',
														onChange: (e) => onChangeFieldInput(e, idx, 'Job Name'),
														onKeyDown: (e) => onChangeFieldKeyDown(e),
														...autocompleteTextFieldProps
													}}
												/>
											</Grid>
										)}

										{filter.filterType == 'Dataset' && (
											<Grid item xs={5}>
												<CustomAutocomplete
													options={datasetNameList}
													noOptionsText={datasetNoOptionsText}
													value={filter.keywords}
													onClose={(e, val) => handleOnClose(e, idx, val)}
													onChange={(e, val) => handleChangeDynamicKeywords(e, idx, val, 'Dataset')}
													sx={autocompleteSxProps}
													customTextFieldProps={{
														placeholder: 'Search Keywords',
														onChange: (e) => onChangeFieldInput(e, idx, 'Dataset'),
														onKeyDown: (e) => onChangeFieldKeyDown(e),
														...autocompleteTextFieldProps
													}}
												/>
											</Grid>
										)}

										{filter.filterType == 'Workspace' && (
											<Grid item xs={5}>
												<CustomAutocomplete
													options={workspaceNameList}
													noOptionsText={workspaceNoOptionsText}
													value={filter.keywords}
													onClose={(e, val) => handleOnClose(e, idx, val)}
													onChange={(e, val) => handleChangeDynamicKeywords(e, idx, val, 'Workspace')}
													sx={autocompleteSxProps}
													customTextFieldProps={{
														placeholder: 'Search Keywords',
														onChange: (e) => onChangeFieldInput(e, idx, 'Workspace'),
														onKeyDown: (e) => onChangeFieldKeyDown(e),
														...autocompleteTextFieldProps
													}}
												/>
											</Grid>
										)}

										{filter.filterType == 'Capacity' && (
											<Grid item xs={5}>
												<CustomAutocomplete
													options={capacityNameList}
													noOptionsText={capacityNoOptionsText}
													value={filter.keywords}
													onClose={(e, val) => handleOnClose(e, idx, val)}
													onChange={(e, val) => handleChangeDynamicKeywords(e, idx, val, 'Capacity')}
													sx={autocompleteSxProps}
													customTextFieldProps={{
														placeholder: 'Search Keywords',
														onChange: (e) => onChangeFieldInput(e, idx, 'Capacity'),
														onKeyDown: (e) => onChangeFieldKeyDown(e),
														...autocompleteTextFieldProps
													}}
												/>
											</Grid>
										)}

										<Grid item xs={0.5} display="flex" justifyContent="end">
											<OutlinedButton
												sx={{
													borderColor: 'rgba(254, 38, 38, 1)'
												}}
												onClick={(e) => {
													handleRemoveFilter(idx);
												}}
											>
												<CloseIcon sx={{ color: 'rgba(254, 38, 38, 1)' }} />
											</OutlinedButton>
										</Grid>

										<Grid item xs={0.5} display="flex" justifyContent="end">
											<OutlinedButton
												sx={{
													borderColor: 'rgba(0, 155, 216, 1)'
												}}
												onClick={(e) => {
													handleAddFilter(idx);
												}}
											>
												<AddIcon sx={{ color: 'rgba(0, 155, 216, 1)' }} />
											</OutlinedButton>
										</Grid>
									</React.Fragment>
								);
							})}
					</Grid>
				</Grid>
			</WidgetCardContainer>

			{!!searchScheduleBtn && (
				<Stack>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="pageTitle" sx={{ fontSize: '16px', fontWeight: '700', color: `${colors.table.title} !important`  }}>Select Jobs From Search Results</Typography>
						{tableDataList && tableDataList.length > 0 && (
							<Button disabled={!selectedRows.length} variant="outlined" startIcon={<PlusSymbolIcon />} className={classes.addSchBtn} onClick={addSchedulesToProject}
								sx={{ borderRadius: '4px', '&:hover': { backgroundColor: '#009BD8', color: '#fff !important' }, '&.Mui-disabled': { opacity: '0.6' }, '&:hover .MuiButton-startIcon svg path': { fill: '#fff !important' } }}>
								ADD ALL SELECTED
							</Button>
						)}
					</Box>
					<Box id={`sch_list_details${prjid}`}>
						{(() => {
							let content;
							if (!tableLoading && tableDataList.length) {
								content = (
									<CustomizedDataGrid
										tableColumns={platformName == 'Power BI' ? getPowerBiColumns(handlePowerBiRowCheckboxChange) : getColumns(handleRowCheckboxChange)}
										tableData={tableDataList}
										tableType="add-schedules"
										defaultColumnNames={platformName == 'Power BI' ? PowerBiColumnNames : defaultColumnNames}
										page={page}
										pageSize={pageSize}
										paginationData={paginationData}
										listLength={tableDataList.length}
										onHeaderSelectAll={handleHeaderSelectAll}
										handlepageSizeChange={handlepageSizeChange}
										handlePageChange={handlePageChange}
										isDateRangeFilter={false}
										isDisabled={false}
										isFetching={tableLoading}
									/>
								);
							} else if (!tableLoading && tableDataList.length === 0) {
								content = (
									<Typography textAlign="center" sx={{ color: `${colors.table.text} !important` }}>No result found</Typography>
								);
							} else {
								content = <Loader />;
							}
							return content;
						})()}
					</Box>
				</Stack>
			)}
			<Stack pt={1}>
				{addedSchedulesList && addedSchedulesList.length > 0 && !scheduleTableLoading && (
					<Stack pt={1}>
						<Typography variant="pageTitle" style={{ fontSize: '16px', fontWeight: '700', color: `${colors.table.title}` }}>Added Jobs</Typography>
						{addedSchedulesList?.length ? (
							<Box className='grid-added-jobs'>
								<CustomizedDataGrid
									tableColumns={getAddedScheduleColumns(toggleSubRow, isAllowed, handleTableAction)}
									tableData={addedSchedulesList}
									tableType="added-schedules"
									defaultColumnNames={defaultAddedScheduleColumnNames}
									onSorting={handleSorting}
									currSorting={sortingInfo}
									onFilterCheck={handleFilter}
									currFilter={filterInfo}
									filterDataOptions={filterData}
									page={pageSchedule}
									pageSize={pageSizeSchedule}
									paginationData={paginationDataSchedule}
									listLength={addedSchedulesList.length}
									handlepageSizeChange={handlepageSizeChangeSchedule}
									handlePageChange={handlePageChangeSchedule}
									isDateRangeFilter={false}
									isDisabled={false}
									isFetching={scheduleTableLoading}
									onFilterSearch={handleFilterSearch}
								/>
							</Box>
						) : (
							<Typography textAlign="center" sx={{ color: `${colors.table.text} !important` }}>Data will be displayed based on selected filters.</Typography>
						)}
					</Stack>
				)}
				{(dependentInfoLoading || scheduleTableLoading) &&
					<Stack justifyContent="center" alignItems="center" height="10vh">
						<Typography variant="pageTitle" color="dark.main">
							Loading...
						</Typography>
					</Stack>
				}
			</Stack>
			<Dialog open={deleteModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
				<DataPipelineModal handleEditClose={handleEditClose} programData={deleteModal}
					getToastData={getToastData}
					passInfoToParent={passInfoToParent}
				/>
			</Dialog>
			<Dialog open={bulkUploadModal} onClose={() => setBulkUploadModal(false)} fullWidth={true} maxWidth="md">
				<JobBulkUpload setBulkUploadModal={setBulkUploadModal} setSelectedRows={setSelectedRows} addSchedulesToProject={addSchedulesToProject} addProjectLoading={addProjectLoading} successMsg={successMsg} successCount={successCount} getAddedScheduleList={getAddedScheduleList} />
			</Dialog>
			<Snackbar
				open={toast && openModal.message}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				autoHideDuration={6000}
				onClick={handleClose}
				message={openModal.message}
			/>
			<DialogModal
				isOpen={openModal.isOpen}
				handleClose={handleModalClose}
				isShowCloseButton={!searchScheduleBtn}
				title={addProjectLoading ? 'Confirmation' : 'Jobs Added Successfully'}
			>
				{addProjectLoading ? (
					<Box sx={{ p: 6, textAlign: 'center' }}>
						<LoaderComponent size={24} />
					</Box>
				) : (
					<Box sx={{ p: 4, textAlign: 'center' }}>

						{successMsg === 'Schedules already added!' ? (
							<Typography sx={{ textAlign: 'center', mb: 2 }}>{successMsg}</Typography>
						) : (
							<Box sx={{ flexGrow: 1 }}>
								<Grid container spacing={2}>
									<Grid item xs={1.5}>
										<Box sx={{ width: '65px', height: '65px', '& svg': { maxWidth: '100%' } }}><SuccessTicketIcon /></Box>
									</Grid>
									<Grid item xs={10}>
										<Box sx={{ textAlign: 'left', mt: 1, fontSize: '28px', fontWeight: '500' }}>
											{selectedRows.length} {selectedRows.length > 1 ? 'Jobs' : 'Job'} added successfully in the Project.</Box>
										<Box sx={{ textAlign: 'left', mt: 1, fontSize: '18px', fontWeight: '400' }}>Would you like to add more?</Box>
									</Grid>
								</Grid>
							</Box>
						)}

						{successMsg === 'Schedules already added!' ? (
							<Box sx={{ textAlign: 'right', mt: 2 }}>
								<Button
									variant="pepwiseOutline"
									onClick={handleModalClose}
									sx={{
										color: 'info.main'
									}}
								>
									Close
								</Button>
							</Box>
						) : (
							<Box sx={{ textAlign: 'right', mt: 3 }}>
								<Button
									variant="pepwiseOutline"
									component={Link}
									to={`/data-pipeline/projects/${prgmid}/${prjid}/${prgname}/${prjname}/${moment().tz('America/Chicago').format("YYYY-MM-DD")}`}
									sx={{
										color: 'info.main'
									}}
								>
									SAVE & EXIT
								</Button>
								<Button onClick={handleAddMore} variant="pepwiseSecondary">
									ADD MORE
								</Button>
							</Box>
						)}
					</Box>
				)}
			</DialogModal>
		</Stack>
	);
};

export default AddSchedules;
